// ============================================================
// LearningJourney.jsx
// ============================================================
// Junior-dev Notes:
// - Theme: High-contrast Gold on Neural Purple for consistency.
// - Layout: Chronological roadmap using Flexbox gap and Box shadows.
// - Identity: Integrated Finnish Engineering Standards badge.
// - Clean Code: Mapped data structure to avoid repetitive JSX.
// ============================================================

import React from "react";
import {
    Box,
    Card,
    Typography,
    Grid,
    Avatar,
    styled,
    Container,
    Chip,
    Stack,
} from "@mui/material";

/* Icons */
import SchoolIcon from "@mui/icons-material/School";
import CodeIcon from "@mui/icons-material/Code";
import PsychologyIcon from "@mui/icons-material/Psychology";
import TerminalIcon from "@mui/icons-material/Terminal";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

/* ── Brand tokens ────────────────────────────────────────── */
const GOLD = "#E5C95F";
const GOLD_MUTED = "rgba(229, 201, 95, 0.2)";
const NAVY_DEEP = "#100720";
const FINLAND_BLUE = "#003580";
const TEXT_MUTED = "rgba(255, 255, 255, 0.7)";

/* ✅ Assets */
const bgImage = "https://images.unsplash.com/photo-1499951360447-b19be8fe80f5";

/* ── Styled Components ───────────────────────────────────── */

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.92), rgba(16,7,32,0.96)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "100px",
    paddingTop: theme.spacing(12),
    color: "white",
}));

const RoadmapCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(255,255,255,0.03)",
    borderRadius: "16px",
    padding: "30px",
    marginBottom: "25px",
    boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
    display: "flex",
    alignItems: "center",
    gap: "25px",
    border: `1px solid ${GOLD_MUTED}`,
    transition: "0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    "&:hover": {
        transform: "translateX(10px)",
        borderColor: GOLD,
        backgroundColor: "rgba(255,255,255,0.05)",
    },
    [theme.breakpoints.down("sm")]: {
        flexDirection: "column",
        textAlign: "center",
        padding: "20px",
    },
}));

const IconWrapper = styled(Avatar)(({ theme }) => ({
    backgroundColor: GOLD_MUTED,
    color: GOLD,
    width: 80,
    height: 80,
    border: `1px solid ${GOLD}`,
}));

const FinnishBadge = styled(Box)({
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "6px 12px",
    borderRadius: "4px",
    border: `1px solid rgba(255,255,255,0.1)`,
    backgroundColor: "rgba(255,255,255,0.03)",
    marginTop: "20px"
});

/* ── Main Component ──────────────────────────────────────── */

export default function LearningJourney() {
    const roadmap = [
        {
            icon: <SchoolIcon sx={{ fontSize: 35 }} />,
            title: "1. Foundations",
            description:
                "Master core programming logic: variables, data types, loops, and control structures. This is where your engineering mindset begins.",
        },
        {
            icon: <PsychologyIcon sx={{ fontSize: 35 }} />,
            title: "2. Computer Science Essentials",
            description:
                "Deep dive into algorithms, data structures, and time complexity (Big O). Understand how memory and CPU work together.",
        },
        {
            icon: <CodeIcon sx={{ fontSize: 35 }} />,
            title: "3. Professional OOP",
            description:
                "Master Object-Oriented Principles: Inheritance, Encapsulation, Polymorphism, and Abstraction across Java, C++, and Python.",
        },
        {
            icon: <TerminalIcon sx={{ fontSize: 35 }} />,
            title: "4. Backend & Systems",
            description:
                "Build scalable APIs and server-side logic. Learn REST, secure authentication, and relational database management (MySQL/MongoDB).",
        },
        {
            icon: <WorkspacePremiumIcon sx={{ fontSize: 35 }} />,
            title: "5. Industrial Projects",
            description:
                "Apply your knowledge to full-stack capstone projects. Build real-world systems that prove your technical competence.",
        },
        {
            icon: <CheckCircleIcon sx={{ fontSize: 35 }} />,
            title: "6. Certification & Integrity",
            description:
                "Validate your expertise through our proctored secure exams. Earn your Finnish Engineering Standard certificate.",
        },
        {
            icon: <RocketLaunchIcon sx={{ fontSize: 35 }} />,
            title: "7. Career Launch",
            description:
                "Interview preparation, portfolio optimization, and LinkedIn branding to land your role in the global tech ecosystem.",
        },
    ];

    return (
        <PageWrapper>
            <Container maxWidth="lg">
                {/* ✅ Header Section */}
                <Box sx={{ textAlign: "center", mb: 8 }}>
                    <Typography
                        variant="h3"
                        sx={{
                            fontWeight: 900,
                            color: GOLD,
                            textTransform: "uppercase",
                            letterSpacing: "-1px",
                            mb: 2
                        }}
                    >
                        THE LEARNING <span style={{ color: "white" }}>ROADMAP</span>
                    </Typography>

                    <Chip
                        label="PRODEV CONSULT · CURRICULUM BLUEPRINT"
                        sx={{
                            bgcolor: "rgba(229, 201, 95, 0.1)",
                            color: GOLD,
                            fontWeight: 800,
                            border: `1px solid ${GOLD}`
                        }}
                    />

                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <FinnishBadge>
                            <Box sx={{ width: 18, height: 12, position: 'relative', bgcolor: '#fff' }}>
                                <Box sx={{ position: 'absolute', top: '40%', width: '100%', height: '20%', bgcolor: FINLAND_BLUE }} />
                                <Box sx={{ position: 'absolute', left: '30%', width: '15%', height: '100%', bgcolor: FINLAND_BLUE }} />
                            </Box>
                            <Typography variant="caption" sx={{ fontWeight: 800, color: "#fff", opacity: 0.8 }}>
                                FINNISH ENGINEERING STANDARDS
                            </Typography>
                        </FinnishBadge>
                    </Box>
                </Box>

                {/* ✅ Roadmap Steps */}
                <Grid container spacing={2}>
                    {roadmap.map((step, index) => (
                        <Grid item xs={12} key={index}>
                            <RoadmapCard elevation={0}>
                                <IconWrapper>{step.icon}</IconWrapper>

                                <Box>
                                    <Typography
                                        variant="h5"
                                        sx={{ fontWeight: 800, color: GOLD, mb: 1 }}
                                    >
                                        {step.title}
                                    </Typography>

                                    <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.7, fontSize: '1rem' }}>
                                        {step.description}
                                    </Typography>
                                </Box>
                            </RoadmapCard>
                        </Grid>
                    ))}
                </Grid>
            </Container>
        </PageWrapper>
    );
}
